import { useNavigate } from "react-router-dom";
import useFetch from "@/lib/useFetch";
import { PageHeader, Loading, ErrorState, EmptyState, Avatar } from "@/components/common";
import Crumbs from "@/components/Crumbs";
import { saveCurrentScroll, useScrollRestore } from "@/lib/scrollRestore";
import { ShieldAlert } from "@/components/icons";

const PROBLEMA_LABEL = {
  cnpj_ausente: { l: "CNPJ ausente", c: "bg-bad-soft text-bad" },
  cnpj_invalido: { l: "CNPJ inválido", c: "bg-bad-soft text-bad" },
  cnpj_duplicado: { l: "CNPJ duplicado", c: "bg-warn-soft text-warn" },
  email_invalido: { l: "E-mail inválido", c: "bg-warn-soft text-warn" },
  email_de_usuario: { l: "E-mail é de um usuário", c: "bg-bad-soft text-bad" },
};

export default function AuditoriaCadastro() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useFetch("/admin/auditoria-cadastro");
  useScrollRestore(!loading && !error);

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} />;

  const grupos = data?.grupos || [];
  const openCliente = (id) => { saveCurrentScroll(); navigate(`/cliente/${id}`); };

  return (
    <div>
      <Crumbs items={[{ label: "Auditoria de cadastro" }]} testId="audit-crumbs" />
      <PageHeader
        title="Auditoria de cadastro"
        subtitle={`${data?.clientes_analisados === 1 ? "1 cliente analisado" : `${data?.clientes_analisados || 0} clientes analisados`} · ${data?.total === 1 ? "1 problema encontrado" : `${data?.total || 0} problemas encontrados`}`}
      />
      <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="audit-warn">
        <b>Somente listagem — nenhum registro é alterado.</b> Corrija cada cadastro no fluxo normal (editar cliente) para preservar histórico e a trava de 90 dias por CNPJ.
      </div>

      {grupos.length === 0 ? (
        <EmptyState icon={ShieldAlert} title="Base limpa" subtitle="Nenhum cliente com CNPJ ausente/inválido/duplicado ou e-mail inválido." />
      ) : (
        <div className="space-y-4">
          {grupos.map((g) => (
            <div key={g.owner_id || "sem_owner"} className="bg-card rounded-2xl border border-black/5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] overflow-hidden" data-testid={`audit-grupo-${g.owner_id || "none"}`}>
              <div className="flex items-center gap-3 px-5 py-4 border-b border-black/5 bg-slate-50/60">
                <Avatar name={g.owner_name} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy truncate">{g.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{g.count === 1 ? "1 cliente com problemas" : `${g.count} clientes com problemas`}</div>
                </div>
              </div>
              <ul className="divide-y divide-black/5">
                {g.items.map((it) => (
                  <li key={it.cliente_id} onClick={() => openCliente(it.cliente_id)} data-testid={`audit-item-${it.cliente_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-navy truncate">{it.nome}{it.empresa ? ` · ${it.empresa}` : ""}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        CNPJ: {it.cnpj || "—"} · E-mail: {it.email || "—"}
                      </div>
                      {it.detalhes?.email_de_usuario && (
                        <div className="text-xs text-bad mt-0.5" data-testid={`audit-email-user-${it.cliente_id}`}>
                          Bate com o usuário <b>{it.detalhes.email_de_usuario.user_name}</b> ({it.detalhes.email_de_usuario.user_email})
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0 justify-end">
                      {it.problemas.map((p) => {
                        const info = PROBLEMA_LABEL[p] || { l: p, c: "bg-slate-100 text-slate-600" };
                        return <span key={p} className={`text-xs font-medium px-2 py-0.5 rounded-full ${info.c}`}>{info.l}</span>;
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
